import React, { useState, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import styled, { keyframes } from "styled-components";
import { Layers, Sparkles, ArrowRight } from "lucide-react";

// --- Animations ---
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const aurora = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: #000;
  color: #fff;
  overflow: hidden;

  @media (max-width: 960px) {
    flex-direction: column;
    overflow-y: auto; /* Allow scrolling on mobile */
  }
`;

const BrandSection = styled.div`
  flex: 1.2;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px;
  background: radial-gradient(circle at 50% 50%, #1a1a1a 0%, #000 100%);
  overflow: hidden;

  /* Aurora Effect */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      #000000,
      #2d1b4e, /* Deep Purple */
      #000000,
      #1a3c34  /* Deep Emerald */
    );
    background-size: 300% 300%;
    animation: ${aurora} 15s ease infinite;
    opacity: 0.6;
    z-index: 0;
  }

  @media (max-width: 960px) {
    padding: 30px 20px; /* Reduced from 40px */
    flex: none; /* Disable flex scaling */
    height: 40vh; /* Fixed height for brand section */
    min-height: 300px;
  }
`;

const BrandContent = styled.div`
  position: relative;
  z-index: 10;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 960px) {
    animation: none; /* Disable animation on mobile to prevent visibility issues */
    opacity: 1;
  }
`;

const LogoIcon = styled.div`
  width: 60px; /* Reduced from 80px */
  height: 60px;
  background: linear-gradient(135deg, #fff, #a5a5a5);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px; /* Reduced from 32px */
  box-shadow: 0 20px 40px rgba(0,0,0,0.5);
  animation: ${float} 6s ease-in-out infinite;
  
  color: #000;

  @media (min-width: 961px) {
      width: 80px;
      height: 80px;
      margin-bottom: 32px;
  }
`;

const MainHeading = styled.h1`
  font-size: 4rem;
  font-weight: 900;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(to right, #fff, #b3b3b3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 960px) {
    font-size: 2.2rem; /* Reduced from 2.8rem */
    margin-bottom: 12px;
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #a3a3a3;
  max-width: 500px;
  line-height: 1.6;
  margin-bottom: 40px;

  @media (max-width: 960px) {
    font-size: 0.9rem; /* Reduced from 1rem */
    margin-bottom: 20px;
    line-height: 1.4;
  }
`;

const LoginSection = styled.div`
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #0a0a0a;
  position: relative;

  @media (max-width: 960px) {
    flex: 1;
    background: #000;
    /* Removed negative margin to simplify layout */
    border-radius: 0; 
    z-index: 20;
    padding: 24px;
    min-height: 60vh; /* Ensure login area takes rest of screen */
  }
`;

const GlassCard = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 48px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 1s ease-out 0.2s backwards;

  @media (max-width: 960px) {
    animation: none; /* Disable animation */
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
    background: transparent; /* Remove card bg on mobile for cleaner look */
    border: none;
    box-shadow: none;
    backdrop-filter: none; /* Disable blur to prevent crash on some Android devices */
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const WelcomeText = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
`;

const InstructionText = styled.p`
  font-size: 0.95rem;
  color: #666;
`;

const GuestButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 12px;
  color: #888;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: #555;
    color: #fff;
    background: rgba(255,255,255,0.05);
  }
`;

// 간단한 JWT 디코더 (base64url)
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function LoginPage({ onLoginSuccess }) {
  const handleGoogleSuccess = (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      alert("구글 로그인 토큰이 없습니다.");
      return;
    }

    // 1. JWT 로컬 디코딩 (UI 빠른 업데이트용)
    const decoded = parseJwt(idToken);

    // 2. 백엔드 검증 요청
    import("./services/apiService").then(({ default: apiService }) => {
      apiService.loginWithGoogle(idToken)
        .then(response => {
          if (response.success) {
            console.log("Backend auth success:", response.user);
            const user = {
              ...response.user,
              rawIdToken: idToken
            };

            try {
              localStorage.setItem("user", JSON.stringify(user));
            } catch (e) {
              // 저장 실패 시 무시
            }

            if (onLoginSuccess) onLoginSuccess(user);
          } else {
            alert("서버 인증 실패: " + response.message);
          }
        })
        .catch(err => {
          console.error("Login API Error:", err);
          alert("로그인 처리 중 오류가 발생했습니다.");
        });
    });
  };

  const handleGoogleFailure = (error) => {
    alert("구글 로그인 실패: " + (error?.error || JSON.stringify(error)));
  };

  const handleGuestLogin = () => {
    const guestUser = {
      id: 'temp00000',
      displayName: 'Visiter',
      email: 'guest@temp.com',
      isGuest: true
    };
    localStorage.setItem("guestLastLogin", new Date().toISOString());

    localStorage.setItem("user", JSON.stringify(guestUser));
    if (onLoginSuccess) onLoginSuccess(guestUser);
  };

  return (
    <PageContainer>
      <BrandSection>
        <BrandContent>
          <LogoIcon>
            <Layers size={48} strokeWidth={2.5} />
          </LogoIcon>
          <MainHeading>
            Don't memorize.<br />
            Just Press.
          </MainHeading>
          <SubHeading>
            NUREUM VOCA is the most intuitive way<br />
            to master a new language.
          </SubHeading>
        </BrandContent>
      </BrandSection>

      <LoginSection>
        <GlassCard>
          <LoginHeader>
            <WelcomeText>Welcome Back</WelcomeText>
            <InstructionText>Sign in to continue your journey</InstructionText>
          </LoginHeader>

          <div style={{ width: '100%', marginBottom: '24px' }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleFailure}
              width="100%"
              theme="filled_black" // Dark theme button
              shape="pill"
              text="continue_with"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#333', margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
            <span style={{ fontSize: '12px', color: '#555' }}>OR</span>
            <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
          </div>

          <GuestButton onClick={handleGuestLogin}>
            <span>Continue as Guest</span>
            <ArrowRight size={16} />
          </GuestButton>

        </GlassCard>
      </LoginSection>
    </PageContainer>
  );
}

export default LoginPage;